import { zodResolver } from '@hookform/resolvers/zod';
import {
  AlertTriangle,
  BellRing,
  Bot,
  CheckCircle2,
  Clock,
  History,
  Search,
  X,
  XCircle,
  Zap,
} from 'lucide-react';
import { useDeferredValue, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ExportMenu } from '../components/ExportMenu';
import { PostPicker } from '../components/PostPicker';
import { Topbar } from '../components/Topbar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Empty } from '../components/ui/empty';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Spinner } from '../components/ui/spinner';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../components/ui/toast';
import {
  type AutoBreakingDecisionItem,
  type AutoBreakingDecisionsResponse,
  type AutoBreakingOutcome,
  useAutoBreakingDecisions,
  useCancelBreakingPush,
  useLastBroadcast,
  usePushHistory,
  useSetAutoBreakingEnabled,
  useTriggerBreakingNews,
} from '../lib/api/breaking';
import { PreviousBroadcastNotice } from '../components/PreviousBroadcastNotice';
import { exportPushes } from '../lib/api/export';
import { fmtDateTime, fmtLagos, fmtRelative } from '../lib/date';
import { useLocaleStore } from '../lib/locales';
import type { PushStatus } from '../lib/types';
import { truncate } from '../lib/utils';

const schema = z.object({
  type: z.enum(['post', 'page']).default('post'),
  slug: z.string().min(1),
  locale: z.string().regex(/^[a-z]{2,8}$/),
  delayMinutes: z.coerce
    .number()
    .int()
    .nonnegative()
    .max(60 * 24)
    .optional(),
});
type Form = z.input<typeof schema>;

export default function BreakingNews(): JSX.Element {
  return (
    <>
      <Topbar title="Breaking news" />
      <Tabs defaultValue="compose">
        <div className="rail-accent mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-line bg-surface px-5 py-2.5">
          <span className="min-w-0 truncate text-[12.5px] font-medium leading-tight text-ink-muted">
            Compose a broadcast or audit past pushes.
          </span>
          <div className="ml-auto">
            <TabsList>
              <TabsTrigger value="compose">
                <BellRing className="h-3.5 w-3.5" /> Compose
              </TabsTrigger>
              <TabsTrigger value="auto">
                <Bot className="h-3.5 w-3.5" /> Auto signals
              </TabsTrigger>
              <TabsTrigger value="history">
                <History className="h-3.5 w-3.5" /> History
              </TabsTrigger>
            </TabsList>
          </div>
        </div>

        <TabsContent value="compose">
          <ComposePanel />
        </TabsContent>
        <TabsContent value="auto">
          <AutoSignalsPanel />
        </TabsContent>
        <TabsContent value="history">
          <HistoryPanel />
        </TabsContent>
      </Tabs>
    </>
  );
}


function ComposePanel(): JSX.Element {
  const locale = useLocaleStore((s) => s.current);
  const available = useLocaleStore((s) => s.available);
  const trigger = useTriggerBreakingNews();
  const { showToast } = useToast();
  const [confirmImmediateOpen, setConfirmImmediateOpen] = useState(false);
  const [confirmQueueOpen, setConfirmQueueOpen] = useState(false);

  const form = useForm<Form>({
    resolver: zodResolver(schema),
    // Keeps what the user has typed when this form re-syncs from the
    // server — a refetch or another admin's edit updates only the
    // fields they haven't touched, instead of wiping the whole form.
    resetOptions: { keepDirtyValues: true },
    values: { type: 'post', slug: '', locale, delayMinutes: undefined },
  });

  // Fire the recency lookup only when EITHER confirmation dialog is open
  // — keeps the form quiet during normal compose typing and ensures the
  // data is fresh at the moment the editor needs it.
  const slugWatch = form.watch('slug');
  const localeWatch = form.watch('locale');
  const typeWatch = form.watch('type');
  const lastBroadcast = useLastBroadcast(
    slugWatch || null,
    localeWatch,
    (typeWatch ?? 'post') as 'post' | 'page',
    { enabled: (confirmImmediateOpen || confirmQueueOpen) && !!slugWatch },
  );

  async function submitBroadcast(immediate: boolean): Promise<void> {
    const v = schema.parse(form.getValues());
    try {
      const resp = await trigger.mutateAsync({ ...v, immediate });
      const supersedeNote =
        resp.supersededCount > 0
          ? ` Cancelled ${resp.supersededCount} earlier in-flight push${resp.supersededCount === 1 ? '' : 'es'}.`
          : '';
      if (immediate) {
        const reach =
          typeof resp.deliveredCount === 'number'
            ? ` Reached ~${resp.deliveredCount.toLocaleString()} device${resp.deliveredCount === 1 ? '' : 's'}.`
            : '';
        setConfirmImmediateOpen(false);
        showToast({
          title: 'Push sent',
          description: `Broadcast fired now.${reach}${supersedeNote}`,
          variant: 'success',
        });
      } else {
        setConfirmQueueOpen(false);
        showToast({
          title: 'Push queued',
          description: `Will fire ${fmtLagos(resp.scheduledAt, 'PP p')}.${supersedeNote}`,
          variant: 'success',
        });
      }
    } catch (err) {
      showToast({
        title: immediate ? 'Send failed' : 'Failed',
        description: (err as Error).message,
        variant: 'danger',
      });
    }
  }

  async function openImmediateConfirm(): Promise<void> {
    const valid = await form.trigger();
    if (valid) setConfirmImmediateOpen(true);
  }

  async function openQueueConfirm(): Promise<void> {
    const valid = await form.trigger();
    if (valid) setConfirmQueueOpen(true);
  }

  return (
    <div className="grid grid-cols-1 gap-6 px-6 pb-10 pt-2 lg:grid-cols-[1fr_360px]">
      <Card>
        <CardHeader>
          <CardTitle>Compose</CardTitle>
          <CardDescription>
            Pick the post (by slug + locale). Apps subscribe to <code>breaking-news-{locale}</code>.
            The server reads the post's title and image when sending.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={form.handleSubmit(() => void openQueueConfirm())}
            className="grid gap-4"
          >
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Item type</Label>
                <Select
                  value={form.watch('type')}
                  onValueChange={(v) => form.setValue('type', v as 'post' | 'page', { shouldDirty: true })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="post">Post</SelectItem>
                    <SelectItem value="page">Page</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Locale</Label>
                <Select
                  value={form.watch('locale')}
                  onValueChange={(v) => form.setValue('locale', v, { shouldDirty: true })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {available.map((l) => (
                      <SelectItem key={l.code} value={l.code}>
                        {l.code.toUpperCase()} · {l.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Post</Label>
              <PostPicker
                type={form.watch('type') ?? 'post'}
                locale={form.watch('locale')}
                value={form.watch('slug')}
                onChange={(slug) => form.setValue('slug', slug, { shouldValidate: !!slug, shouldDirty: true })}
              />
              {form.formState.errors.slug ? (
                <p className="mt-1 text-xs text-danger">Please select a post.</p>
              ) : null}
            </div>

            <div>
              <Label>
                Delay (minutes) <span className="text-ink-subtle">— optional override</span>
              </Label>
              <Input
                type="number"
                min={0}
                max={60 * 24}
                placeholder="Leave blank for the configured default"
                {...form.register('delayMinutes')}
              />
              <p className="mt-1 text-xs text-ink-subtle">
                0 = immediate. Default is the value of <code>BREAKING_NEWS_DELAY_MINUTES</code>.
              </p>
            </div>

            <div className="flex flex-wrap justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => void openImmediateConfirm()}
                disabled={trigger.isPending}
              >
                <Zap className="h-4 w-4" />
                Send now
              </Button>
              <Button type="submit" disabled={trigger.isPending}>
                <BellRing className="h-4 w-4" />
                {trigger.isPending ? 'Working…' : 'Queue broadcast'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>How this works</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-ink-muted">
          <p className="flex items-start gap-2">
            <Clock className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
            <span>
              <strong>Queue broadcast</strong> holds the push for a few minutes before delivery —
              if the post is removed before the delay elapses, the push is cancelled automatically.
              Use it as your default.
            </span>
          </p>
          <p className="flex items-start gap-2">
            <Zap className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <span>
              <strong>Send now</strong> bypasses the queue and broadcasts inline. Use it when speed
              matters more than the retraction window — once it fires you cannot recall it from
              devices.
            </span>
          </p>
          <p className="pl-6">
            The notification body is built from the post's title and image (CDN URL). Targeting is
            by FCM topic <code>breaking-news-LOCALE</code>; only subscribers for that locale receive
            it.
          </p>
          <p className="pl-6">
            You can also broadcast directly from a post's detail page using the "Broadcast as
            breaking news" action. Re-broadcasting the same post cancels the in-flight push first
            (visible in the History tab as <code>cancelled</code>) and queues a fresh one.
          </p>
        </CardContent>
      </Card>

      <Dialog
        open={confirmImmediateOpen}
        onOpenChange={(o) => !trigger.isPending && setConfirmImmediateOpen(o)}
      >
        <DialogContent size="md">
          <DialogHeader>
            <p className="eyebrow">
              <Zap className="h-3 w-3" /> Immediate breaking-news push
            </p>
            <DialogTitle>Send this push right now?</DialogTitle>
            <DialogDescription>
              Confirm before broadcasting to every subscriber on the{' '}
              {form.watch('locale').toUpperCase()} channel.
            </DialogDescription>
          </DialogHeader>

          <div className="-mx-2 flex-1 space-y-3 overflow-y-auto px-2">
            <div className="rounded-md border border-line bg-surface-tinted/40 px-3 py-2.5">
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-subtle">
                Post
              </p>
              <p className="mt-0.5 truncate font-mono text-[12.5px] text-ink">
                {form.watch('slug') || '—'}
              </p>
            </div>

            {lastBroadcast.data ? (
              <PreviousBroadcastNotice info={lastBroadcast.data} />
            ) : null}

            <ul className="space-y-1.5 text-[12.5px] leading-snug text-ink-muted">
              <li className="flex gap-2">
                <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-brand/60" />
                <span>
                  Reaches{' '}
                  <strong className="text-ink">
                    every device subscribed to{' '}
                    <code className="font-mono">breaking-news-{form.watch('locale')}</code>
                  </strong>{' '}
                  — anonymous installs included.
                </span>
              </li>
              <li className="flex gap-2">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" />
                <span>
                  No retraction window — the push fires immediately and cannot be recalled from
                  devices.
                </span>
              </li>
            </ul>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setConfirmImmediateOpen(false)}
              disabled={trigger.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void submitBroadcast(true)}
              disabled={trigger.isPending}
            >
              <Zap className="h-3.5 w-3.5" />
              {trigger.isPending ? 'Sending…' : 'Yes, send now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Queue Broadcast confirm — mirrors Send-Now structure but with the
       *  delay-window framing. The PreviousBroadcastNotice is the load-bearing
       *  bit: surfaces "this post was already pushed N min ago" so the editor
       *  doesn't accidentally re-push and double-buzz subscribers. */}
      <Dialog
        open={confirmQueueOpen}
        onOpenChange={(o) => !trigger.isPending && setConfirmQueueOpen(o)}
      >
        <DialogContent size="md">
          <DialogHeader>
            <p className="eyebrow">
              <BellRing className="h-3 w-3" /> Queue breaking-news push
            </p>
            <DialogTitle>Queue this broadcast?</DialogTitle>
            <DialogDescription>
              Held for the configured delay before delivery — cancellable from the
              History tab during that window.
            </DialogDescription>
          </DialogHeader>

          <div className="-mx-2 flex-1 space-y-3 overflow-y-auto px-2">
            <div className="rounded-md border border-line bg-surface-tinted/40 px-3 py-2.5">
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-subtle">
                Post
              </p>
              <p className="mt-0.5 truncate font-mono text-[12.5px] text-ink">
                {form.watch('slug') || '—'}
              </p>
            </div>

            {lastBroadcast.data ? (
              <PreviousBroadcastNotice info={lastBroadcast.data} />
            ) : null}

            <ul className="space-y-1.5 text-[12.5px] leading-snug text-ink-muted">
              <li className="flex gap-2">
                <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-brand/60" />
                <span>
                  Reaches{' '}
                  <strong className="text-ink">
                    every device subscribed to{' '}
                    <code className="font-mono">breaking-news-{form.watch('locale')}</code>
                  </strong>{' '}
                  — anonymous installs included.
                </span>
              </li>
              <li className="flex gap-2">
                <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand" />
                <span>
                  Held in the retraction window before firing — cancel from{' '}
                  <strong className="text-ink">History</strong> if you change your mind.
                </span>
              </li>
            </ul>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setConfirmQueueOpen(false)}
              disabled={trigger.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void submitBroadcast(false)}
              disabled={trigger.isPending}
            >
              <BellRing className="h-3.5 w-3.5" />
              {trigger.isPending ? 'Queuing…' : 'Yes, queue broadcast'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function HistoryPanel(): JSX.Element {
  const available = useLocaleStore((s) => s.available);
  const [statusFilter, setStatusFilter] = useState<'all' | PushStatus>('all');
  const [localeFilter, setLocaleFilter] = useState<'all' | string>('all');
  const [origin, setOrigin] = useState<'all' | 'manual' | 'auto'>('all');
  const [slugQ, setSlugQ] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const deferredSlug = useDeferredValue(slugQ);
  const { showToast } = useToast();
  const cancelMut = useCancelBreakingPush();
  // Track which row's confirm dialog is open. Using a single id rather
  // than a per-row state map so only one dialog is ever in flight, which
  // matches user expectation in a long table.
  const [cancelTarget, setCancelTarget] = useState<{
    id: string;
    title: string;
    locale: string;
  } | null>(null);

  const list = usePushHistory({
    status: statusFilter === 'all' ? undefined : statusFilter,
    locale: localeFilter === 'all' ? undefined : localeFilter,
    manualOnly: origin === 'manual' ? true : undefined,
    postSlug: deferredSlug.trim() || undefined,
    page,
    pageSize,
  });

  const handleCancelConfirm = async () => {
    if (!cancelTarget) return;
    try {
      const res = await cancelMut.mutateAsync(cancelTarget.id);
      if (res.cancelled) {
        showToast({
          title: 'Push cancelled',
          description: `${cancelTarget.locale.toUpperCase()} · ${cancelTarget.title}`,
          variant: 'success',
        });
      } else {
        // Race with the scheduler — the row already fired or was already
        // cancelled by the time we got here. Surface honestly so the
        // editor doesn't think it worked when it didn't.
        showToast({
          title: 'Too late',
          description: 'The push fired or was already cancelled before we could stop it.',
          variant: 'danger',
        });
      }
    } catch (err) {
      showToast({
        title: 'Cancel failed',
        description: (err as Error).message,
        variant: 'danger',
      });
    } finally {
      setCancelTarget(null);
    }
  };

  // Auto-only filter is client-side because the server flag is "manual only".
  const items = (list.data?.items ?? []).filter((it) => {
    if (origin === 'auto' && it.manual) return false;
    return true;
  });

  const total = list.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <>
    <div className="space-y-4 px-6 pb-10 pt-2">
      <Card>
        <CardContent className="flex flex-wrap items-center gap-2 py-3">
          <div className="relative min-w-[240px] flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle" />
            <Input
              placeholder="Filter by post slug…"
              value={slugQ}
              onChange={(e) => {
                setSlugQ(e.target.value);
                setPage(1);
              }}
              className="pl-8"
            />
          </div>
          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setStatusFilter(v as typeof statusFilter);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={origin}
            onValueChange={(v) => {
              setOrigin(v as typeof origin);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All origins</SelectItem>
              <SelectItem value="manual">Manual</SelectItem>
              <SelectItem value="auto">Auto</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={localeFilter}
            onValueChange={(v) => {
              setLocaleFilter(v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Locale" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All locales</SelectItem>
              {available.map((l) => (
                <SelectItem key={l.code} value={l.code}>
                  {l.code.toUpperCase()}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ExportMenu
            options={[
              {
                format: 'csv',
                label: 'CSV (current filters)',
                description:
                  'Audit-friendly: status, post, scheduled, sent, cancelled, triggered by',
                run: () =>
                  exportPushes({
                    format: 'csv',
                    status: statusFilter === 'all' ? undefined : statusFilter,
                    locale: localeFilter === 'all' ? undefined : localeFilter,
                    manualOnly: origin === 'manual' ? true : undefined,
                    postSlug: deferredSlug.trim() || undefined,
                  }),
              },
              {
                format: 'json',
                label: 'JSON (current filters)',
                run: () =>
                  exportPushes({
                    format: 'json',
                    status: statusFilter === 'all' ? undefined : statusFilter,
                    locale: localeFilter === 'all' ? undefined : localeFilter,
                    manualOnly: origin === 'manual' ? true : undefined,
                    postSlug: deferredSlug.trim() || undefined,
                  }),
              },
            ]}
          />
        </CardContent>
      </Card>

      {list.isLoading ? (
        <Spinner label="Loading history…" />
      ) : Array.isArray(list.data?.items) && items.length > 0 ? (
        <div className="overflow-hidden rounded-lg border border-line bg-surface shadow-card">
          <table className="table-base">
            <thead>
              <tr>
                <th>Post</th>
                <th className="w-24">Locale</th>
                <th className="w-28">Status</th>
                <th className="w-24">Origin</th>
                <th className="w-44">When</th>
                <th className="w-44">Triggered by</th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id}>
                  <td>
                    <p className="font-medium text-ink">
                      {truncate(it.post?.title ?? '— deleted post —', 80)}
                    </p>
                    <p className="font-mono text-xs text-ink-subtle">{it.post?.slug ?? '—'}</p>
                  </td>
                  <td>
                    <Badge variant="outline" className="uppercase">
                      {it.post?.locale ?? '—'}
                    </Badge>
                  </td>
                  <td>
                    <StatusBadge status={it.status} />
                  </td>
                  <td>
                    <Badge variant={it.manual ? 'brand' : 'neutral'}>
                      {it.manual ? 'Manual' : 'Auto'}
                    </Badge>
                  </td>
                  <td className="text-sm text-ink-muted">
                    <p title={fmtDateTime(it.createdAt)}>queued {fmtRelative(it.createdAt)}</p>
                    {it.status === 'pending' ? (
                      <p className="text-xs text-ink-subtle" title={fmtDateTime(it.scheduledAt)}>
                        fires {fmtRelative(it.scheduledAt)}
                      </p>
                    ) : it.status === 'sent' && it.sentAt ? (
                      <p className="text-xs text-ink-subtle" title={fmtDateTime(it.sentAt)}>
                        sent {fmtRelative(it.sentAt)}
                      </p>
                    ) : it.status === 'cancelled' && it.cancelledAt ? (
                      <p className="text-xs text-ink-subtle" title={fmtDateTime(it.cancelledAt)}>
                        cancelled {fmtRelative(it.cancelledAt)}
                      </p>
                    ) : null}
                  </td>
                  <td className="text-sm text-ink-muted">
                    {it.triggeredBy ? (
                      <>
                        <p className="text-ink">{it.triggeredBy.name ?? it.triggeredBy.email}</p>
                        {it.triggeredBy.name ? (
                          <p className="truncate text-xs text-ink-subtle">{it.triggeredBy.email}</p>
                        ) : null}
                      </>
                    ) : (
                      <span className="text-ink-subtle">
                        {it.manual ? 'admin token' : 'system'}
                      </span>
                    )}
                  </td>
                  <td className="text-right">
                    {it.status === 'pending' ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setCancelTarget({
                            id: it.id,
                            title: it.post?.title ?? '— deleted post —',
                            locale: it.post?.locale ?? '—',
                          })
                        }
                        title="Cancel this pending push before it fires"
                      >
                        <X className="h-3.5 w-3.5" />
                        Cancel
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-between border-t border-line px-4 py-3 text-sm text-ink-muted">
            <span>
              Page {page} of {totalPages} · {total.toLocaleString()} total
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <Empty
          icon={History}
          title="No broadcasts yet"
          description="Past breaking-news pushes will appear here once you queue your first one."
        />
      )}
    </div>

    <Dialog
      open={cancelTarget !== null}
      onOpenChange={(o) => !cancelMut.isPending && !o && setCancelTarget(null)}
    >
      <DialogContent size="sm">
        <DialogHeader>
          <p className="eyebrow">
            <X className="h-3 w-3" /> Cancel pending push
          </p>
          <DialogTitle>Cancel this broadcast?</DialogTitle>
          <DialogDescription>
            Stops the push from firing. No notification will reach subscribers.
          </DialogDescription>
        </DialogHeader>

        <div className="-mx-2 flex-1 space-y-3 overflow-y-auto px-2">
          {cancelTarget ? (
            <div className="rounded-md border border-line bg-surface-tinted/40 px-3 py-2.5">
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-subtle">
                {cancelTarget.locale.toUpperCase()} · pending
              </p>
              <p className="mt-0.5 line-clamp-3 text-[13.5px] font-semibold leading-snug text-ink">
                {cancelTarget.title}
              </p>
            </div>
          ) : null}
          <p className="text-[12px] leading-snug text-ink-muted">
            The push appears in History as <code className="font-mono">cancelled</code>.
            If the scheduler beats us to it, you'll get a "too late" notice instead.
          </p>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => setCancelTarget(null)}
            disabled={cancelMut.isPending}
          >
            Keep it
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={() => void handleCancelConfirm()}
            disabled={cancelMut.isPending}
          >
            <X className="h-3.5 w-3.5" />
            {cancelMut.isPending ? 'Cancelling…' : 'Yes, cancel push'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

function StatusBadge({ status }: { status: PushStatus }): JSX.Element {
  if (status === 'sent') {
    return (
      <Badge variant="success">
        <CheckCircle2 className="h-3 w-3" /> Sent
      </Badge>
    );
  }
  if (status === 'cancelled') {
    return (
      <Badge variant="danger">
        <XCircle className="h-3 w-3" /> Cancelled
      </Badge>
    );
  }
  return (
    <Badge variant="warning">
      <Clock className="h-3 w-3" /> Pending
    </Badge>
  );
}

// ─── Auto signals panel ───────────────────────────────────────────────────

function AutoSignalsPanel(): JSX.Element {
  const locale = useLocaleStore((s) => s.current);
  const [outcomeFilter, setOutcomeFilter] = useState<'all' | AutoBreakingOutcome>('all');
  const decisions = useAutoBreakingDecisions({
    locale,
    outcome: outcomeFilter === 'all' ? undefined : outcomeFilter,
    limit: 100,
  });

  const cfg = decisions.data?.config;
  const cache = decisions.data?.cache;

  return (
    <div className="grid grid-cols-1 gap-4 px-6 pb-10 pt-2 lg:grid-cols-[1fr_320px]">
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-ink-subtle">
              Last 100 decisions
            </p>
            <span className="text-[12px] text-ink-muted">·</span>
            <span className="font-mono text-[10.5px] uppercase tracking-[0.12em] text-ink-muted">
              locale {locale.toUpperCase()}
            </span>
          </div>
          <Select value={outcomeFilter} onValueChange={(v) => setOutcomeFilter(v as typeof outcomeFilter)}>
            <SelectTrigger className="h-8 w-[180px] text-[12.5px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All outcomes</SelectItem>
              <SelectItem value="pushed">Pushed</SelectItem>
              <SelectItem value="below_threshold">Below threshold</SelectItem>
              <SelectItem value="manual_cooldown">Manual cooldown</SelectItem>
              <SelectItem value="daily_cap">Daily cap</SelectItem>
              <SelectItem value="dedup">Dedup</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {decisions.isLoading ? (
          <div className="rounded-lg border border-line bg-surface p-6"><Spinner label="Loading…" /></div>
        ) : !decisions.data || decisions.data.items.length === 0 ? (
          <Empty
            title="No decisions yet"
            description="The auto pipeline records every candidate it scores. Once posts start arriving while it's enabled, they'll appear here."
          />
        ) : (
          <ul className="space-y-2">
            {decisions.data.items.map((d) => (
              <DecisionRow key={d.id} d={d} />
            ))}
          </ul>
        )}
      </div>

      <aside className="space-y-3">
        <PipelineToggleCard cfg={cfg} />
        <Card>
          <CardHeader>
            <CardTitle>News-desk cache</CardTitle>
            <CardDescription>Powers the echo signals; refreshed every 10 min.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-[13px]">
            <KeyVal label="Cached items" value={cache ? `${cache.items.toLocaleString()}` : '—'} />
            <KeyVal
              label="Last refresh"
              value={cache?.lastRefreshAt ? fmtRelative(cache.lastRefreshAt) : '—'}
            />
          </CardContent>
        </Card>
      </aside>
    </div>
  );
}

function KeyVal({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: 'success' | 'danger';
}): JSX.Element {
  return (
    <div className="flex items-center justify-between border-b border-line-hair pb-1.5 last:border-b-0 last:pb-0">
      <span className="font-mono text-[10.5px] uppercase tracking-[0.12em] text-ink-subtle">{label}</span>
      <span
        className={
          accent === 'success'
            ? 'font-medium text-success'
            : accent === 'danger'
              ? 'font-medium text-danger'
              : 'font-medium text-ink'
        }
      >
        {value}
      </span>
    </div>
  );
}

function DecisionRow({ d }: { d: AutoBreakingDecisionItem }): JSX.Element {
  const passed = d.outcome === 'pushed';
  return (
    <li className="rounded-lg border border-line bg-surface p-3 shadow-card">
      <div className="flex items-start gap-3">
        <ScoreChip score={d.score} threshold={d.threshold} passed={passed} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <p className="truncate text-[13px] font-medium text-ink">
              {d.post ? d.post.title : '(post deleted)'}
            </p>
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-ink-subtle">
              {d.locale}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <SignalChip label="cat" value={d.signals.category} />
            <SignalChip label="kw" value={d.signals.keyword} />
            <SignalChip label="echo" value={d.signals.newsDeskEcho} />
            <SignalChip label="flag" value={d.signals.editorFlagEcho} />
            <SignalChip label="llm" value={d.signals.llm} />
            <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.12em] text-ink-subtle">
              {fmtDateTime(d.decidedAt)}
            </span>
          </div>
          <OutcomeLine outcome={d.outcome} reason={d.reason} signals={d.signals} />
        </div>
      </div>
    </li>
  );
}

function ScoreChip({
  score,
  threshold,
  passed,
}: {
  score: number;
  threshold: number;
  passed: boolean;
}): JSX.Element {
  return (
    <div
      className={
        'shrink-0 rounded-md border px-2 py-1.5 text-center ' +
        (passed
          ? 'border-success/30 bg-success/8 text-success'
          : 'border-line bg-surface-tinted text-ink-muted')
      }
    >
      <p className="font-heading text-[15px] font-bold leading-none tabular">{score}</p>
      <p className="mt-0.5 font-mono text-[8.5px] uppercase tracking-[0.14em] opacity-70">
        / {threshold}
      </p>
    </div>
  );
}

function SignalChip({ label, value }: { label: string; value: number }): JSX.Element {
  if (value === 0) {
    return (
      <span className="rounded-sm bg-surface-tinted px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.1em] text-ink-subtle/70">
        {label} 0
      </span>
    );
  }
  return (
    <span className="rounded-sm bg-brand/10 px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-brand">
      {label} +{value}
    </span>
  );
}

function OutcomeLine({
  outcome,
  reason,
  signals,
}: {
  outcome: AutoBreakingOutcome;
  reason: string | null;
  signals: AutoBreakingDecisionItem['signals'];
}): JSX.Element {
  const map: Record<AutoBreakingOutcome, { label: string; tone: 'success' | 'warning' | 'danger' | 'neutral' }> = {
    pushed: { label: 'Pushed', tone: 'success' },
    below_threshold: { label: 'Below threshold', tone: 'neutral' },
    manual_cooldown: { label: 'Manual cooldown', tone: 'warning' },
    daily_cap: { label: 'Daily cap', tone: 'warning' },
    dedup: { label: 'Dedup (already queued)', tone: 'neutral' },
    disabled: { label: 'Pipeline disabled', tone: 'neutral' },
    no_post: { label: 'Post not found', tone: 'danger' },
  };
  const m = map[outcome];
  const detail = signals.details ?? {};
  const hits = [
    detail.keywordMatches ? `kw:[${String(detail.keywordMatches)}]` : '',
    detail.newsDeskEchoSource ? `echo: ${truncate(String(detail.newsDeskEchoSource), 60)}` : '',
    detail.editorFlagEchoSource ? `flag: ${truncate(String(detail.editorFlagEchoSource), 60)}` : '',
    detail.llmVerdict ? `${detail.llmVerdict}` : '',
  ].filter(Boolean);
  return (
    <p
      className={
        'mt-1 flex flex-wrap items-center gap-1.5 text-[11.5px] ' +
        (m.tone === 'success'
          ? 'text-success'
          : m.tone === 'warning'
            ? 'text-warning'
            : m.tone === 'danger'
              ? 'text-danger'
              : 'text-ink-muted')
      }
    >
      <span className="font-semibold">{m.label}</span>
      {reason ? <span className="font-mono text-[10.5px]">— {reason}</span> : null}
      {hits.length > 0 ? <span className="font-mono text-[10.5px] text-ink-subtle">· {hits.join(' · ')}</span> : null}
    </p>
  );
}

/** Pipeline status + interactive on/off toggle. Replaces the static read-only
 *  status card so admins can disable the bot without redeploying. */
function PipelineToggleCard({
  cfg,
}: {
  cfg: AutoBreakingDecisionsResponse['config'] | undefined;
}): JSX.Element {
  const setEnabled = useSetAutoBreakingEnabled();
  const { showToast } = useToast();

  const handleToggle = async (next: boolean) => {
    try {
      await setEnabled.mutateAsync(next);
      showToast({
        title: next ? 'Auto pipeline enabled' : 'Auto pipeline disabled',
        description: next
          ? 'New posts will now be scored against the rule + LLM signals.'
          : 'No new auto pushes will be queued. Manual Compose is unaffected.',
        variant: 'success',
      });
    } catch (err) {
      showToast({
        title: 'Toggle failed',
        description: (err as Error).message,
        variant: 'danger',
      });
    }
  };

  const enabled = cfg?.enabled ?? false;
  const sourceMeta =
    cfg?.enabledSource === 'admin'
      ? `set by ${cfg.enabledUpdatedBy ?? 'admin'}${cfg.enabledUpdatedAt ? ` · ${fmtRelative(cfg.enabledUpdatedAt)}` : ''}`
      : 'env default';

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pipeline status</CardTitle>
        <CardDescription>Toggle the auto pipeline on or off without a redeploy.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div
          className={
            'flex items-center justify-between rounded-lg border p-3 ' +
            (enabled
              ? 'border-success/30 bg-success/5'
              : 'border-line bg-surface-tinted/40')
          }
        >
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-ink">
              Auto pipeline {enabled ? 'enabled' : 'disabled'}
            </p>
            <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.12em] text-ink-subtle">
              {sourceMeta}
            </p>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={handleToggle}
            disabled={setEnabled.isPending || !cfg}
            aria-label="Toggle auto breaking-news pipeline"
          />
        </div>

        <div className="space-y-2 text-[13px]">
          <KeyVal label="Score threshold" value={cfg ? `${cfg.threshold}` : '—'} />
          <KeyVal label="Daily cap (per locale)" value={cfg ? `${cfg.dailyCapPerLocale}` : '—'} />
          <KeyVal label="Manual cooldown" value={cfg ? `${cfg.manualCooldownMinutes} min` : '—'} />
          <KeyVal
            label="LLM tiebreaker"
            value={
              cfg?.llmConfigured
                ? `On · ${cfg.llmModel}`
                : cfg?.llmEnabled && !cfg?.groqApiKeyPresent
                  ? 'Enabled — GROQ_API_KEY missing'
                  : !cfg?.llmEnabled && cfg?.groqApiKeyPresent
                    ? 'Off — flag is false (key present)'
                    : 'Off'
            }
            accent={
              cfg?.llmConfigured
                ? 'success'
                : cfg?.llmEnabled && !cfg?.groqApiKeyPresent
                  ? 'danger'
                  : undefined
            }
          />
        </div>

        <p className="rounded-md bg-surface-tinted/50 px-2.5 py-2 text-[11.5px] text-ink-muted">
          Disabling stops new evaluations. Pushes already queued in the delay window keep their
          cancellation lane on the History tab — cancel manually if needed.
        </p>
      </CardContent>
    </Card>
  );
}
